import React, { useState } from 'react';
import { IconDownload } from "@tabler/icons-react";
import { formatDateToLocal, getContrastColor } from "utils/utils";
import Swal from "sweetalert2";

const ExportScheduleExcel = ({ 
  daysInRange, 
  groupedUsers, 
  scheduleMap, 
  apiStats, 
  shifts, 
  filterOptions 
}) => {
  const [isExporting, setIsExporting] = useState(false);
  
  const handleExportToExcel = () => {
    setIsExporting(true);
    
    // Use setTimeout to allow UI to show loading state
    setTimeout(() => {
      try {
        const dateHeaders = daysInRange.map(date => date.getDate().toString());
        const shiftHeaders = (shifts.data || []).map(s => s.code || s.name);
        const header = ['Name', 'WK DO', ...dateHeaders, 'Total OFF', ...shiftHeaders];

        let tableContent = "";

        // Build Table Rows with Styling
        Object.entries(groupedUsers).forEach(([deptName, deptUsers]) => {
          // Department Header Row
          tableContent += `
            <tr style="background-color: #f1f5f9; color: #1e293b;">
              <td colspan="${header.length}" style="padding: 10px; font-weight: bold; border: 1px solid #cbd5e1; text-align: left;">
                ${deptName.toUpperCase()}
              </td>
            </tr>
          `;
          
          deptUsers.forEach(user => {
            let userRow = `<tr><td style="padding: 10px; font-weight: bold; border: 1px solid #cbd5e1; min-width: 180px;">${user.name}</td>`;
            userRow += `<td style="padding: 10px; text-align: center; border: 1px solid #cbd5e1;">${user.day_off || '-'}</td>`;

            // Daily Schedules
            daysInRange.forEach(date => {
              const schedule = scheduleMap.get(user.id)?.get(date.toDateString());
              let bgColor = "#ffffff";
              let textColor = "#64748b";
              let text = "-";

              if (schedule) {
                if (schedule.is_day_off) {
                  bgColor = "#ef4444";
                  textColor = "#ffffff";
                  text = "OFF";
                } else {
                  bgColor = schedule.shift?.color || "#206bc4";
                  textColor = getContrastColor(bgColor);
                  text = schedule.shift?.code || schedule.shift?.name || 'S';
                }
              }

              userRow += `
                <td style="padding: 10px; background-color: ${bgColor}; color: ${textColor}; text-align: center; border: 1px solid #cbd5e1; font-weight: bold; font-size: 14px;">
                  ${text}
                </td>
              `;
            });

            // Stats Part
            const stats = apiStats[user.id];
            userRow += `<td style="padding: 10px; text-align: center; border: 1px solid #cbd5e1; background-color: #f0f7ff; color: #206bc4; font-weight: bold;">${stats?.libur || 0}</td>`;
            
            (shifts.data || []).forEach(shift => {
              const shiftColor = shift.color || "#eee";
              const shiftTextColor = getContrastColor(shiftColor);
              userRow += `<td style="padding: 10px; text-align: center; border: 1px solid #cbd5e1; background-color: ${shiftColor}; color: ${shiftTextColor}; font-weight: bold;">${stats?.shiftCounts?.[shift.id] || 0}</td>`;
            });

            userRow += "</tr>";
            tableContent += userRow;
          });
        });

        const fullHtml = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
            <head>
              <meta charset="UTF-8">
              <style>
                table { border-collapse: collapse; font-family: sans-serif; }
                th { background-color: #1e293b; color: #ffffff; padding: 10px; border: 1px solid #334155; text-align: center; font-size: 14px; }
                td { font-size: 14px; }
              </style>
            </head>
            <body>
              <table>
                <thead>
                  <tr>
                    ${header.map(h => `<th>${h}</th>`).join('')}
                  </tr>
                </thead>
                <tbody>
                  ${tableContent}
                </tbody>
              </table>
            </body>
          </html>
        `;

        const blob = new Blob([fullHtml], { type: 'application/vnd.ms-excel' });
        const startStr = formatDateToLocal(filterOptions.startDate);
        const endStr = formatDateToLocal(filterOptions.endDate);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Schedule_${startStr}_to_${endStr}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Export error:", error);
        Swal.fire("Error", "Failed to export data to Excel", "error");
      } finally {
        setIsExporting(false);
      }
    }, 100);
  };

  return (
    <button 
      className="btn btn-sm btn-green border-0 fw-bold px-3 rounded d-inline-flex align-items-center gap-1 shadow-sm" 
      onClick={handleExportToExcel}
      disabled={isExporting}
    >
      {isExporting ? (
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      ) : (
        <IconDownload size={16} />
      )}
      <span>{isExporting ? "Exporting..." : "Download Excel"}</span>
    </button>
  );
};

export default ExportScheduleExcel;

